/**
 * MiniProjectTaskEditTray — full edit/create experience for a single
 * Mini-Project Task. DEV-1110 Phase 1.
 *
 * Lifts the inline AddTaskForm + EditTaskForm out of TasksSection into
 * a right-edge sliding tray that mirrors GoalEditTray's pattern. Same
 * fields, same template-lock rules, same delete gating — just relocated
 * into the consistent tray surface that Goal already established.
 *
 * Mode-discriminated Props:
 *   - mode="create" : POST /api/mini-projects/<guid>/tasks
 *   - mode="edit"   : PUT  /api/mini-projects/<guid>/tasks/<taskGuid>
 *
 * Template-task semantics (preserved from EditTaskForm):
 *   - isTemplateTask=true locks nameText, description, requiresEvidence
 *   - Delete button is hidden on template tasks
 *   - Italic "template-locked" notice surfaces explanation to the Lead
 *
 * Field set (pure refactor — DEV-1110 scope locked 2026-05-26):
 *   - Create: name, description, dueDate, requiresEvidence
 *   - Edit:   name (locked if template), description (locked if template),
 *             dueDate, notes, requiresEvidence (locked if template)
 *
 * No subsections — field count is low (≤5) so a flat column reads
 * cleaner than forcing Goal-tray-style section labels.
 *
 * Status pill stays on the card (MiniProjectTaskCard inline cycle) —
 * not migrated into the tray. The card's pencil is what opens this
 * tray; the pill is its own quick-cycle affordance.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  createMiniProjectTask,
  updateMiniProjectTask,
  deleteMiniProjectTask,
  transitionMiniProjectTask,
  fetchMiniProjectTeamMembers,
  type MiniProjectTask,
  type MiniProjectTeamMember,
} from "../../api/miniProjects";
import {
  fetchMiniProjectRoles,
  type MiniProjectRole,
} from "../../api/miniProjectRoles";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import {
  parseInstructionSteps,
  serializeInstructionSteps,
  newInstructionStep,
  isSafeHttpUrl,
  type InstructionStep,
} from "../../Data/MiniProjectConfig/instructionSteps";
import { Tray } from "../Tray/Tray";
import styles from "./MiniProjectTaskEditTray.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

/**
 * Tray serves both create and edit flows. Mode-specific differences:
 *   - Title:  "Add task" vs "Edit task: <name>"
 *   - Notes:  edit-only field (matches pre-DEV-1110 behavior — create
 *             form historically lacked notes; preserving exactly to
 *             stay inside "pure refactor" scope)
 *   - Delete: edit-only, gated on !isTemplateTask
 *   - Save handler: createMiniProjectTask vs updateMiniProjectTask
 */
type Props =
  | {
      mode: "edit";
      open: boolean;
      task: MiniProjectTask | null;
      miniProjectGuid: string;
      onClose: () => void;
      onMutated: () => void;
      /** DEV-1257-followup. When false, the tray renders structural fields
       *  read-only and hides Save/Delete — the assignee can still cycle
       *  status via the pill (backend allows via CanUpdateOwnTaskStatus).
       *  Defaults to true for backward compat. */
      isLead?: boolean;
    }
  | {
      mode: "create";
      open: boolean;
      miniProjectGuid: string;
      onClose: () => void;
      onMutated: () => void;
      isLead?: boolean;
    };

/** Status cycle order — matches MiniProjectTaskCard so the tray's
 *  quick-cycle is identical to the card's. */
const NEXT_STATUS: Record<MiniProjectTask["status"], MiniProjectTask["status"]> = {
  not_started: "in_progress",
  in_progress: "done",
  done: "not_started",
};

const STATUS_LABEL_KEY: Record<MiniProjectTask["status"], string> = {
  not_started: "miniProject.cards.task.status.notStarted",
  in_progress: "miniProject.cards.task.status.inProgress",
  done: "miniProject.cards.task.status.done",
};

/* ── Inline icons ──────────────────────────────────────────────── */

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* ── Component ─────────────────────────────────────────────────── */

const MiniProjectTaskEditTray: React.FC<Props> = (props) => {
  const { mode, open, onClose, onMutated, miniProjectGuid } = props;
  const { t } = useAppTranslation("pages");

  // Mode-discriminated value extraction. Edit derives initial state
  // from the task prop; create starts from blank defaults. The parent
  // keys this component on taskGuid (edit) or "create-<bool>" (create)
  // so useState initializers fire fresh on each open.
  const task = mode === "edit" ? props.task : null;
  // DEV-1279 (Skip, 2026-06-09): the template-task lock is retired — tasks are
  // fully editable on the master (authoring) AND on instances (the owner's
  // project to adapt). Kept as a named flag, now always false, so the
  // downstream field/delete gates collapse cleanly to "editable" without
  // restructuring the tray. (The header doc-comment's lock notes are stale.)
  const isTemplate = false;
  // DEV-1257-followup. Default true preserves the existing Lead-only call
  // sites. Card now passes the actual value so non-Lead assignees opening
  // the tray see a read-only structural view but can still cycle status.
  const isLead = props.isLead ?? true;

  // ── Local state ──
  const [nameText, setNameText] = useState<string>(task?.nameText ?? "");
  const [description, setDescription] = useState<string>(task?.description ?? "");
  const [dueDate, setDueDate] = useState<string>(task?.dueDate ?? "");
  const [notes, setNotes] = useState<string>(task?.notes ?? "");
  const [requiresEvidence, setRequiresEvidence] = useState<boolean>(
    task?.requiresEvidence ?? false,
  );
  // Setter is intentionally unused while the Evidence-Required checkbox
  // is hidden (2026-05-27 — see the commented block in the JSX below).
  // The `requiresEvidence` value still round-trips through save so
  // existing values on legacy tasks aren't overwritten.
  void setRequiresEvidence;
  // DEV-1124 (Knowledge Flywheel Phase 1) — optional external reference
  // URL. Free-form; null/empty = no link. Locked for template tasks
  // alongside name/description/evidence.
  const [howToUrl, setHowToUrl] = useState<string>(task?.howToUrl ?? "");

  // DEV-1289 — instruction steps (checklist). Authored here by the Lead;
  // checked off by the assignee in the detail tray. Shared model/shape.
  const [steps, setSteps] = useState<InstructionStep[]>(
    () => parseInstructionSteps(task?.instructionSteps),
  );
  const updateStep = useCallback(
    (id: string, patch: Partial<InstructionStep>) =>
      setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s))),
    [],
  );
  const removeStep = useCallback(
    (id: string) => setSteps((prev) => prev.filter((s) => s.id !== id)),
    [],
  );
  const moveStep = useCallback(
    (id: string, dir: -1 | 1) =>
      setSteps((prev) => {
        const i = prev.findIndex((s) => s.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= prev.length) return prev;
        const next = prev.slice();
        [next[i], next[j]] = [next[j], next[i]];
        return next;
      }),
    [],
  );

  // DEV-1123 (A) — polymorphic single assignee. Encoded as a string for
  // the <select>: "" = unassigned (UI defaults to Lead), "u:<guid>" =
  // team-member assignee, "r:<guid>" = role-slot assignee. Initialized
  // from whichever assignee field on the task is non-null.
  const initialAssigneeValue =
    task?.ownerUserGuid
      ? `u:${task.ownerUserGuid}`
      : task?.assignedRoleGuid
        ? `r:${task.assignedRoleGuid}`
        : "";
  const [assigneeValue, setAssigneeValue] = useState<string>(initialAssigneeValue);

  // DEV-1123 (A) — team + role lookups for the dropdown. Fetched on open
  // (cheap; team is usually <10 members and roles <5 slots). Re-fetch
  // on every open to pick up any changes since last edit.
  const [teamMembers, setTeamMembers] = useState<MiniProjectTeamMember[]>([]);
  const [roles, setRoles] = useState<MiniProjectRole[]>([]);
  const { securityUsers } = useApiSecurityData();

  useEffect(() => {
    if (!open || !miniProjectGuid) return;
    let cancelled = false;
    (async () => {
      try {
        const [tm, rs] = await Promise.all([
          fetchMiniProjectTeamMembers(miniProjectGuid),
          fetchMiniProjectRoles(miniProjectGuid),
        ]);
        if (!cancelled) {
          setTeamMembers(tm);
          setRoles(rs);
        }
      } catch {
        // Non-fatal — the dropdown just renders empty groups.
      }
    })();
    return () => { cancelled = true; };
  }, [open, miniProjectGuid]);

  /** Resolve a user GUID to a display name via the global security cache. */
  const lookupUserName = useCallback((userGuid: string): string => {
    const rows = (securityUsers || []) as Array<Record<string, unknown>>;
    const target = userGuid.toUpperCase();
    const user = rows.find((u) => String(u.user_guid).toUpperCase() === target);
    return (
      (user?.display_name as string) ||
      (user?.name as string) ||
      (user?.screen_name as string) ||
      userGuid
    );
  }, [securityUsers]);

  /** Parse the encoded assignee string into the backend payload shape. */
  const assigneePayload = useMemo(() => {
    if (assigneeValue.startsWith("u:")) {
      return { userGuid: assigneeValue.slice(2), roleGuid: null as string | null };
    }
    if (assigneeValue.startsWith("r:")) {
      return { userGuid: null as string | null, roleGuid: assigneeValue.slice(2) };
    }
    return { userGuid: null as string | null, roleGuid: null as string | null };
  }, [assigneeValue]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Save (branches on mode) ──
  const handleSave = useCallback(async () => {
    // Name validation: required for create, and for edit on non-template
    // tasks (template tasks lock the name field anyway so we can't blank it).
    const needsName = mode === "create" || !isTemplate;
    if (needsName && !nameText.trim()) {
      setError(t("miniProject.sections.error.nameRequired"));
      return;
    }
    // DEV-1289 — only http/https step links are allowed (blocks javascript:/
    // data: scheme injection). Validate before save; the detail tray also
    // refuses to render an unsafe URL as a link.
    const badStep = steps.find((s) => s.url && s.url.trim() && !isSafeHttpUrl(s.url));
    if (badStep) {
      setError(t("miniProject.sections.tasks.stepUrlInvalid"));
      return;
    }
    const stepsJson = serializeInstructionSteps(steps);
    setBusy(true);
    setError(null);
    try {
      if (mode === "create") {
        await createMiniProjectTask(miniProjectGuid, {
          nameText: nameText.trim(),
          description: description.trim() || undefined,
          dueDate: dueDate || undefined,
          requiresEvidence,
          // DEV-1124: optional how-to link on creation. undefined skips
          // the field; empty string would also be ignored server-side.
          howToUrl: howToUrl.trim() || undefined,
          // DEV-1289 — instruction steps checklist.
          instructionSteps: stepsJson,
          // DEV-1123 (A) — assignee on create. At most one will be set;
          // backend XOR enforces the constraint.
          ownerUserGuid: assigneePayload.userGuid ?? undefined,
          assignedRoleGuid: assigneePayload.roleGuid ?? undefined,
        });
      } else if (task) {
        // Template tasks lock name / description / evidence. Server is
        // the source of truth on the lock; we just skip sending those
        // fields if locked so a tampered payload still can't slip past.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const patch: any = {
          notes: notes,
          dueDate: dueDate || undefined,
          // DEV-1289 — instruction steps (checklist). Sent on every update;
          // operational, not gated by the (retired) template lock.
          instructionSteps: stepsJson,
          // DEV-1123 (A) — assignee is operational, not structural. Sent
          // on every update regardless of template lock. The nested
          // Assignee object replaces BOTH polymorphic columns atomically
          // (null inside = explicit clear); the controller validates XOR.
          assignee: {
            userGuid: assigneePayload.userGuid,
            roleGuid: assigneePayload.roleGuid,
          },
        };
        if (!isTemplate) {
          patch.nameText = nameText.trim() || undefined;
          patch.description = description.trim() || undefined;
          patch.requiresEvidence = requiresEvidence;
          // DEV-1124: send empty string when blank — the server treats
          // "" as "clear to null", but null (the JSON value) is treated
          // as "field absent, leave untouched."
          patch.howToUrl = howToUrl.trim();
        }
        await updateMiniProjectTask(miniProjectGuid, task.taskGuid, patch);
      }
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [
    mode,
    miniProjectGuid,
    task,
    isTemplate,
    nameText,
    description,
    dueDate,
    notes,
    requiresEvidence,
    howToUrl,
    steps,
    assigneePayload,
    onMutated,
    t,
  ]);

  // ── Status cycle (edit-only; persists immediately via transition
  //    endpoint, separate from the draft Save flow). The parent's
  //    onMutated then refetches and the task prop updates with the new
  //    status, so the pill re-renders without remounting the tray.
  const handleCycleStatus = useCallback(async () => {
    if (!task || busy) return;
    setBusy(true);
    setError(null);
    try {
      await transitionMiniProjectTask(
        miniProjectGuid,
        task.taskGuid,
        NEXT_STATUS[task.status],
      );
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [task, busy, miniProjectGuid, onMutated]);

  // ── Delete (gated on edit + !template) ──
  const handleDelete = useCallback(async () => {
    if (!task || isTemplate) return;
    if (!window.confirm(t("miniProject.sections.tasks.confirmDelete"))) return;
    setBusy(true);
    setError(null);
    try {
      await deleteMiniProjectTask(miniProjectGuid, task.taskGuid);
      onMutated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [task, isTemplate, miniProjectGuid, onMutated, t]);

  // ── Title ──
  // In edit mode the body is gated on task != null (the prior task
  // momentarily lingers while the Tray animates closed).
  const bodyOk = mode === "create" || task != null;
  const trayTitle =
    mode === "create"
      ? t("miniProject.cards.task.createTrayTitle")
      : task
        ? t("miniProject.cards.task.editTrayTitle", { name: task.nameText || task.nameKey || "" })
        : t("miniProject.cards.task.createTrayTitle");

  return (
    <Tray open={open} onClose={onClose} title={trayTitle}>
      {bodyOk && (
        <>
          <Tray.Body className={styles.body}>
            {/* Status pill — edit mode only. Cycles status via the
                transition endpoint; persists immediately (does not
                require Save). The card's pill is unreachable behind
                the tray scrim, so the tray needs its own affordance. */}
            {mode === "edit" && task && (
              <div className={styles.row}>
                <label className={styles.label}>
                  {t("miniProject.cards.task.statusLabel")}
                </label>
                <button
                  type="button"
                  className={`${styles.statusPill} ${styles[`status_${task.status}`]}`}
                  onClick={handleCycleStatus}
                  disabled={busy}
                  title={t("miniProject.cards.task.advanceTo", {
                    status: t(STATUS_LABEL_KEY[NEXT_STATUS[task.status]]),
                  })}
                >
                  {t(STATUS_LABEL_KEY[task.status])}
                </button>
              </div>
            )}

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.name")}
              </label>
              <input
                className={styles.input}
                value={nameText}
                onChange={(e) => setNameText(e.target.value)}
                disabled={busy || isTemplate || !isLead}
                placeholder={
                  mode === "create"
                    ? t("miniProject.sections.tasks.namePlaceholder")
                    : undefined
                }
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.description")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <textarea
                className={styles.textarea}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={busy || isTemplate || !isLead}
                placeholder={
                  mode === "create"
                    ? t("miniProject.sections.tasks.descriptionPlaceholder")
                    : undefined
                }
              />
            </div>

            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.dueDate")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                disabled={busy || !isLead}
              />
            </div>

            {/* DEV-1123 (A) — Assignee dropdown. Team members + role
                slots in two optgroups. Unassigned = falls back to Lead. */}
            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.assignee")}
              </label>
              <select
                className={styles.input}
                value={assigneeValue}
                onChange={(e) => setAssigneeValue(e.target.value)}
                disabled={busy || !isLead}
              >
                <option value="">
                  {t("miniProject.sections.tasks.assigneeUnassigned")}
                </option>
                {teamMembers.length > 0 && (
                  <optgroup label={t("miniProject.sections.tasks.assigneeTeamGroup")}>
                    {teamMembers.map((tm) => (
                      <option key={tm.userGuid} value={`u:${tm.userGuid}`}>
                        {lookupUserName(tm.userGuid)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {roles.length > 0 && (
                  <optgroup label={t("miniProject.sections.tasks.assigneeRolesGroup")}>
                    {roles.map((r) => (
                      <option key={r.roleGuid} value={`r:${r.roleGuid}`}>
                        {r.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            {/* DEV-1124 (Knowledge Flywheel Phase 1) — How-to link.
                Free-form URL to a website, YouTube video, etc. Phase 2+
                replaces with first-class how_to entities (DEV-1114). */}
            <div className={styles.row}>
              <label className={styles.label}>
                {t("miniProject.sections.formLabel.howToUrl")}
                <span className={styles.opt}>
                  {t("miniProject.sections.formLabel.optional")}
                </span>
              </label>
              <input
                className={styles.input}
                type="url"
                value={howToUrl}
                onChange={(e) => setHowToUrl(e.target.value)}
                placeholder={t("miniProject.sections.tasks.howToUrlPlaceholder")}
                disabled={busy || isTemplate || !isLead}
              />
            </div>

            {/* DEV-1289 — Instruction steps editor (Lead authoring). Each
                step: label + optional masked link (url + friendly text) +
                a "required" flag that gates task completion. Assignees check
                these off in the detail tray. */}
            {isLead && (
              <div className={styles.row}>
                <label className={styles.label}>
                  {t("miniProject.sections.tasks.stepsLabel")}
                  <span className={styles.opt}>{t("miniProject.sections.formLabel.optional")}</span>
                </label>
                <div className={styles.stepsEditor}>
                  {steps.map((s, i) => (
                    <div key={s.id} className={styles.stepEditRow}>
                      <input
                        className={styles.input}
                        value={s.label}
                        onChange={(e) => updateStep(s.id, { label: e.target.value })}
                        placeholder={t("miniProject.sections.tasks.stepLabelPlaceholder")}
                        disabled={busy}
                      />
                      <div className={styles.stepLinkRow}>
                        <input
                          className={styles.input}
                          type="url"
                          value={s.url ?? ""}
                          onChange={(e) => updateStep(s.id, { url: e.target.value })}
                          placeholder={t("miniProject.sections.tasks.stepUrlPlaceholder")}
                          disabled={busy}
                        />
                        <input
                          className={styles.input}
                          value={s.urlLabel ?? ""}
                          onChange={(e) => updateStep(s.id, { urlLabel: e.target.value })}
                          placeholder={t("miniProject.sections.tasks.stepLinkTextPlaceholder")}
                          disabled={busy}
                        />
                      </div>
                      <div className={styles.stepControls}>
                        <label className={styles.stepRequired}>
                          <input
                            type="checkbox"
                            checked={s.required}
                            onChange={(e) => updateStep(s.id, { required: e.target.checked })}
                            disabled={busy}
                          />
                          {t("miniProject.sections.tasks.stepRequired")}
                        </label>
                        <span className={styles.stepSpacer} />
                        <button
                          type="button"
                          className={styles.stepIconBtn}
                          onClick={() => moveStep(s.id, -1)}
                          disabled={busy || i === 0}
                          aria-label={t("miniProject.sections.tasks.stepMoveUp")}
                          title={t("miniProject.sections.tasks.stepMoveUp")}
                        >↑</button>
                        <button
                          type="button"
                          className={styles.stepIconBtn}
                          onClick={() => moveStep(s.id, 1)}
                          disabled={busy || i === steps.length - 1}
                          aria-label={t("miniProject.sections.tasks.stepMoveDown")}
                          title={t("miniProject.sections.tasks.stepMoveDown")}
                        >↓</button>
                        <button
                          type="button"
                          className={styles.stepRemoveBtn}
                          onClick={() => removeStep(s.id)}
                          disabled={busy}
                          aria-label={t("miniProject.sections.tasks.stepRemove")}
                          title={t("miniProject.sections.tasks.stepRemove")}
                        >✕</button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className={styles.stepAddBtn}
                    onClick={() => setSteps((prev) => [...prev, newInstructionStep()])}
                    disabled={busy}
                  >
                    + {t("miniProject.sections.tasks.stepAdd")}
                  </button>
                </div>
              </div>
            )}

            {/* Notes: edit-only (matches pre-DEV-1110 form set; create
                form never had a notes field). */}
            {mode === "edit" && (
              <div className={styles.row}>
                <label className={styles.label}>
                  {t("miniProject.sections.formLabel.notes")}
                  <span className={styles.opt}>
                    {t("miniProject.sections.formLabel.optional")}
                  </span>
                </label>
                <textarea
                  className={styles.textarea}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={busy || !isLead}
                />
              </div>
            )}

            {/* 2026-05-27 — Evidence-Required checkbox temporarily hidden
                per Skip. The backend gates status='done' on a non-empty
                evidenceAttachmentKeys array
                (MiniProjectTasksController.cs line 220), but the upload
                UI that populates that array was never built — so
                checking this box left tasks un-completable. Restore
                once the upload picker ships.

                The `requiresEvidence` state still round-trips unchanged
                on save (init from task, sent back as-is in the patch
                payload) so pre-existing values on legacy tasks are
                preserved. The setter is a no-op while the UI is hidden;
                see the `void setRequiresEvidence` below.

                Tracking: filed as a follow-up Jira ticket.

                <div className={styles.toggleRow}>
                  <input
                    type="checkbox"
                    checked={requiresEvidence}
                    onChange={(e) => setRequiresEvidence(e.target.checked)}
                    disabled={busy || isTemplate || !isLead}
                  />
                  <div className={styles.toggleText}>
                    <div className={styles.toggleLabel}>
                      {t("miniProject.sections.formLabel.evidenceRequired")}
                    </div>
                  </div>
                </div>
            */}

            {isTemplate && (
              <div className={styles.lockedNote}>
                {t("miniProject.sections.tasks.templateLocked")}
              </div>
            )}

            {error && <div className={styles.errorMsg}>{error}</div>}
          </Tray.Body>

          <Tray.Footer className={styles.footer}>
            {/* Delete: edit mode + non-template + Lead only. */}
            {mode === "edit" && !isTemplate && isLead && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnDanger}`}
                onClick={handleDelete}
                disabled={busy}
              >
                <TrashIcon /> {t("miniProject.sections.button.delete")}
              </button>
            )}
            <button
              type="button"
              className={styles.btn}
              onClick={onClose}
              disabled={busy}
              style={
                mode === "create" || isTemplate
                  ? { marginInlineStart: "auto" }
                  : undefined
              }
            >
              {t("miniProject.sections.button.cancel")}
            </button>
            {/* Save: Lead-only. Non-Lead assignees see the tray as a
                view+status-cycle surface; structural edits are gated above
                and there's nothing to save. DEV-1257-followup. */}
            {isLead && (
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={handleSave}
                disabled={busy}
              >
                {busy
                  ? t("miniProject.sections.button.saving")
                  : mode === "create"
                    ? t("miniProject.sections.tasks.button.add")
                    : t("miniProject.sections.button.save")}
              </button>
            )}
          </Tray.Footer>
        </>
      )}
    </Tray>
  );
};

export default MiniProjectTaskEditTray;
